import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie, ChevronDown } from "lucide-react";

/**
 * Site-wide cookie consent banner: a compact card in the bottom-left corner
 * (full-width bottom card on phones). Shows once until the visitor makes a
 * choice, then remembers it in localStorage so it never nags again. Links to
 * the CMS-managed Cookie and Privacy Policy pages. Rendered inside
 * PublicLayout (not on /admin).
 *
 * "Accept all" and "Reject all" share the same size and weight, as GDPR
 * regulators expect. "Manage preferences" opens per-category switches.
 *
 * Stored keys (analytics/marketing scripts can read these before initialising):
 *   cookieConsent      "accepted" | "declined" | "custom"
 *   cookiePreferences  {"analytics": boolean, "marketing": boolean}
 *
 * Every save also fires a window "cookieconsentchange" event (detail = the
 * preferences object), so Analytics.js can start or stop without a reload.
 *
 * The choice can be changed or withdrawn at any time: openCookiePreferences()
 * (the footer's "Cookie settings" link) reopens the card with the switches
 * showing the stored choice.
 */
const STORAGE_KEY = "cookieConsent";
const PREFS_KEY = "cookiePreferences";
const CONSENT_EVENT = "cookieconsentchange"; // also in Analytics.js
const OPEN_EVENT = "opencookiepreferences";

// Reopen the banner with the preferences expanded (e.g. from the footer).
export const openCookiePreferences = () => {
  try {
    window.dispatchEvent(new Event(OPEN_EVENT));
  } catch (e) {
    /* very old browser without the Event constructor */
  }
};

// The stored per-category choice, or everything off when there is none.
const readStoredPrefs = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
    if (stored && typeof stored === "object") {
      return { analytics: stored.analytics === true, marketing: stored.marketing === true };
    }
    // Older saves only kept the overall choice.
    const all = localStorage.getItem(STORAGE_KEY) === "accepted";
    return { analytics: all, marketing: all };
  } catch (e) {
    return { analytics: false, marketing: false };
  }
};

const CATEGORIES = [
  {
    key: "analytics",
    label: "Analytics",
    description: "Anonymous visit stats that help us improve pages",
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Measures our campaigns and tailors content",
  },
];

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0b72d6]";

// On/off switch for one cookie category.
const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors duration-200 ${
      checked ? "bg-[#f26836]" : "bg-[#c8d2dd]"
    } ${focusRing}`}
  >
    <span
      className={`absolute left-[3px] top-[3px] h-[16px] w-[16px] rounded-full bg-white shadow-[0_1px_2px_rgba(15,44,77,0.3)] transition-transform duration-200 ${
        checked ? "translate-x-[16px]" : "translate-x-0"
      }`}
    />
  </button>
);

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });
  const cardRef = useRef(null);
  const reopened = useRef(false);

  // "Cookie settings" (footer): show the card again with the current choice.
  useEffect(() => {
    const onOpen = () => {
      setPrefs(readStoredPrefs());
      setShowPrefs(true);
      setVisible(true);
      reopened.current = true;
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Move keyboard focus into the card when it was reopened on request.
  useEffect(() => {
    if (visible && reopened.current && cardRef.current) {
      reopened.current = false;
      cardRef.current.focus();
    }
  }, [visible]);

  useEffect(() => {
    // Delay a tick so it doesn't fight the initial paint / cause CLS.
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (!choice) setVisible(true);
    } catch (e) {
      // localStorage unavailable (private mode etc.) — just show the banner.
      setVisible(true);
    }
  }, []);

  const save = (choice) => {
    const value =
      choice.analytics && choice.marketing
        ? "accepted"
        : !choice.analytics && !choice.marketing
        ? "declined"
        : "custom";
    try {
      localStorage.setItem(STORAGE_KEY, value);
      localStorage.setItem(PREFS_KEY, JSON.stringify(choice));
    } catch (e) {
      /* ignore storage errors */
    }
    // Let analytics react straight away (works even if storage failed above).
    try {
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { ...choice } }));
    } catch (e) {
      /* very old browser without CustomEvent — analytics waits for next visit */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-consent-title"
      className="cookie-consent-root fixed z-[60] bottom-[12px] left-[12px] right-[12px] sm:bottom-[24px] sm:left-[24px] sm:right-auto sm:w-[388px]"
    >
      {/* Keyframes for the slide-up entrance, plus the desktop zoom opt-out:
          the public site is zoomed to 0.8 (.public-zoom in index.css), so the
          card counters it to render at native size, like the header does. */}
      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (min-width: 1024px) {
          .public-zoom .cookie-consent-root { zoom: calc(1 / var(--public-zoom)); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cookie-consent-card { animation: none !important; }
        }
      `}</style>

      <div
        ref={cardRef}
        tabIndex={-1}
        className="cookie-consent-card outline-none grid gap-[14px] max-h-[calc(100vh-24px)] sm:max-h-[calc(100vh-48px)] overflow-y-auto rounded-[18px] border border-[#e3e8ef] bg-white p-[20px] text-[#3f4b5b] shadow-[0_1px_2px_rgba(15,44,77,0.06),0_18px_44px_-14px_rgba(15,44,77,0.32)]"
        style={{ animation: "cookieSlideUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both" }}
      >
        {/* Icon + title */}
        <div className="flex items-center gap-[10px]">
          <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#fff0e8] text-[#f26836]">
            <Cookie className="h-[18px] w-[18px]" />
          </span>
          <span
            id="cookie-consent-title"
            className="text-[15.5px] font-semibold tracking-[-0.01em] text-[#0f2c4d]"
          >
            Cookies on Bestowal
          </span>
        </div>

        <p className="m-0 text-[13.5px] leading-[1.6]">
          We use cookies to keep the site secure, measure traffic and improve
          your experience. Read our{" "}
          <Link
            to="/cookie-policy"
            className={`font-semibold text-[#d9541f] underline decoration-1 underline-offset-2 hover:text-[#0f2c4d] transition-colors ${focusRing}`}
          >
            Cookie Policy
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy-policy"
            className={`font-semibold text-[#d9541f] underline decoration-1 underline-offset-2 hover:text-[#0f2c4d] transition-colors ${focusRing}`}
          >
            Privacy Policy
          </Link>
          .
        </p>

        {/* Per-category preferences */}
        {showPrefs && (
          <div
            id="cookie-consent-prefs"
            className="grid rounded-[12px] border border-[#e3e8ef] bg-[#f5f8fb] px-[12px] pt-[4px] pb-[12px]"
          >
            <div className="flex items-center justify-between gap-[14px] border-b border-[#e3e8ef] py-[10px]">
              <div className="min-w-0">
                <span className="block text-[13.5px] font-semibold text-[#0f2c4d]">Essential</span>
                <span className="block text-[12.5px] leading-[1.45] text-[#6b7a8c]">
                  Security, page loading and saving this choice
                </span>
              </div>
              <span className="whitespace-nowrap rounded-full bg-[#e6f5ee] px-[9px] py-[4px] text-[12px] font-semibold text-[#1f7a50]">
                Always on
              </span>
            </div>

            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className="flex items-center justify-between gap-[14px] border-b border-[#e3e8ef] py-[10px]"
              >
                <div className="min-w-0">
                  <span className="block text-[13.5px] font-semibold text-[#0f2c4d]">{cat.label}</span>
                  <span className="block text-[12.5px] leading-[1.45] text-[#6b7a8c]">
                    {cat.description}
                  </span>
                </div>
                <Switch
                  checked={prefs[cat.key]}
                  label={`${cat.label} cookies`}
                  onChange={(on) => setPrefs((p) => ({ ...p, [cat.key]: on }))}
                />
              </div>
            ))}

            <button
              type="button"
              onClick={() => save(prefs)}
              className={`mt-[10px] h-[38px] rounded-[10px] border-[1.5px] border-[#cfd8e3] bg-white px-[18px] text-[14px] font-semibold text-[#0f2c4d] hover:border-[#0f2c4d] transition-colors ${focusRing}`}
            >
              Save my choices
            </button>
          </div>
        )}

        {/* Actions: equal size and weight for reject and accept */}
        <div className="grid grid-cols-2 gap-[8px]">
          <button
            type="button"
            onClick={() => save({ analytics: false, marketing: false })}
            className={`h-[42px] whitespace-nowrap rounded-[10px] bg-[#0f2c4d] px-[18px] text-[14px] font-semibold text-white hover:bg-[#183d66] active:translate-y-px transition-colors ${focusRing}`}
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={() => save({ analytics: true, marketing: true })}
            className={`h-[42px] whitespace-nowrap rounded-[10px] bg-[#f26836] px-[18px] text-[14px] font-semibold text-white shadow-[0_6px_16px_-6px_rgba(242,104,54,0.6)] hover:bg-[#d9541f] active:translate-y-px transition-colors ${focusRing}`}
          >
            Accept all
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowPrefs((s) => !s)}
          aria-expanded={showPrefs}
          aria-controls="cookie-consent-prefs"
          className={`justify-self-start inline-flex items-center gap-[6px] py-[2px] text-[13px] font-semibold text-[#0f2c4d] ${focusRing}`}
        >
          {showPrefs ? "Hide preferences" : "Manage preferences"}
          <ChevronDown
            className={`h-[16px] w-[16px] transition-transform duration-200 ${showPrefs ? "rotate-180" : ""}`}
          />
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
